<script setup lang="ts">
import { type ComponentPublicInstance, computed, nextTick, ref } from 'vue';
import type { IncorporationLine, RequirementSummary } from '@/api/erp-integration';
import ErpIncorporationRow from './erp-incorporation-row.vue';
import { type LineDraft, type RowDraft, lineError, newRow } from './erp-line-draft';
import { quantityText } from './erp-format';

// One Service line of the incorporation screen: what it sells and the
// Requirements it becomes or feeds, one per career on a Package line.

const props = defineProps<{
  line: IncorporationLine;
  index: number;
  requirements: RequirementSummary[];
  showErrors: boolean;
}>();
const draft = defineModel<LineDraft>({ required: true });

const title = computed(() => `Línea ${props.index + 1}`);
const waitingForMap = computed(() => draft.value.unmapped && !draft.value.ownIds.length);
// Only a line not in a Requirement yet can be dismissed.
const dismissible = computed(() => !draft.value.ownIds.length);
const error = computed(() => (props.showErrors ? lineError(draft.value) : null));

function setRow(index: number, row: RowDraft) {
  const rows = [...draft.value.rows];
  rows[index] = row;
  draft.value = { ...draft.value, rows };
}

// Keyboard focus follows each change, so it never falls back to the top of
// the dialog.
const card = ref<ComponentPublicInstance | null>(null);
const rowRefs = new Map<number, { focusFirst: () => Promise<void> }>();

function setRowRef(key: number, component: unknown) {
  if (component) rowRefs.set(key, component as { focusFirst: () => Promise<void> });
  else rowRefs.delete(key);
}

async function focusControl(name: string) {
  await nextTick();
  const root = card.value?.$el as HTMLElement | undefined;
  root?.querySelector<HTMLElement>(`[data-focus="${name}"]`)?.focus();
}

async function addRow() {
  const row = newRow(props.line);
  draft.value = { ...draft.value, rows: [...draft.value.rows, row] };
  await nextTick();
  await rowRefs.get(row.key)?.focusFirst();
}

function removeRow(key: number) {
  draft.value = { ...draft.value, rows: draft.value.rows.filter((row) => row.key !== key) };
  focusControl('add');
}

function setDismissed(dismissed: boolean) {
  draft.value = { ...draft.value, dismissed };
  focusControl(dismissed ? 'undo' : 'dismiss');
}

function rowLabel(index: number): string {
  return draft.value.isPackage ? `la especialidad ${index + 1} de la ${title.value.toLowerCase()}` :
    `la ${title.value.toLowerCase()}`;
}
</script>
<template>
  <v-card
    flat
    class="mb-3 border"
  >
    <v-card-text ref="card">
      <div class="d-flex flex-wrap align-center ga-2 mb-2">
        <h4 class="text-subtitle-2 font-weight-bold">
          {{ title }} · {{ line.productName }}
        </h4>
        <span class="text-body-2 text-medium-emphasis">
          {{ quantityText(line.quantity, line.uom) }}
        </span>
        <v-chip
          v-if="line.rule?.blockHours"
          size="x-small"
          label
        >
          Bloques de {{ line.rule.blockHours }} h
        </v-chip>
        <v-chip
          v-if="draft.isPackage"
          size="x-small"
          label
        >
          Varias especialidades
        </v-chip>
        <v-spacer />
        <v-tooltip
          v-if="dismissible && !draft.dismissed"
          location="top"
          text="Descartar línea"
        >
          <template #activator="{ props: tooltipProps }">
            <v-btn
              v-bind="tooltipProps"
              icon="mdi-delete-outline"
              variant="text"
              size="small"
              color="error"
              data-focus="dismiss"
              :aria-label="`Descartar la ${title.toLowerCase()}`"
              @click="setDismissed(true)"
            />
          </template>
        </v-tooltip>
      </div>
      <div
        v-if="draft.dismissed"
        class="d-flex flex-wrap align-center ga-2"
      >
        <span class="text-body-2 text-medium-emphasis">
          Se descartará al confirmar y no volverá a aparecer.
        </span>
        <v-btn
          size="small"
          variant="text"
          color="primary"
          data-focus="undo"
          :aria-label="`Deshacer el descarte de la ${title.toLowerCase()}`"
          @click="setDismissed(false)"
        >
          Deshacer
        </v-btn>
      </div>
      <template v-else>
        <v-alert
          v-if="waitingForMap"
          type="warning"
          variant="tonal"
          density="compact"
          class="mb-3"
        >
          Producto sin mapear: Kuido no sabe qué atención es. Descarta la línea si no es una
          atención (no vuelve a aparecer), o cancela y pide a Kuido mapear el producto antes de
          incorporar.
        </v-alert>
        <p
          v-if="draft.isPackage && !waitingForMap"
          class="text-body-2 text-medium-emphasis mb-2"
        >
          Un requerimiento por especialidad que indican las notas; todas sus sesiones van a esta
          línea.
        </p>
        <div class="d-flex flex-column ga-3">
          <erp-incorporation-row
            v-for="(row, rowIndex) in draft.rows"
            :key="row.key"
            :ref="(component) => setRowRef(row.key, component)"
            :model-value="row"
            :line="line"
            :label="rowLabel(rowIndex)"
            :requirements="requirements"
            :removable="draft.isPackage"
            :show-errors="showErrors"
            @update:model-value="(value: RowDraft) => setRow(rowIndex, value)"
            @remove="removeRow(row.key)"
          />
        </div>
        <div
          v-if="draft.isPackage"
          class="d-flex flex-wrap ga-2 mt-2"
        >
          <v-btn
            size="small"
            variant="tonal"
            color="primary"
            prepend-icon="mdi-plus"
            data-focus="add"
            :aria-label="`Agregar especialidad a la ${title.toLowerCase()}`"
            @click="addRow"
          >
            Agregar especialidad
          </v-btn>
        </div>
        <div
          v-if="error"
          class="text-body-2 text-error mt-2"
          role="alert"
        >
          {{ error }}
        </div>
      </template>
    </v-card-text>
  </v-card>
</template>
